'use client';

import { useEffect } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { cookiesIdHandler } from 'middleware/cookieMiddlewareIdHandler';
import getFeatures from 'services/growthbook/getFeatures';
import { handleExperimentEvent } from './experiments';

export type growthBookParams = {
  sessionId: string;
  loggedIn: boolean;
  browser: string;
  url: string;
  eventId: string;
};

const isDevMode = process.env.NODE_ENV === 'development';

const growthbookInstance = new GrowthBook({
  enableDevMode: isDevMode || !!getCookie('growthbook_dev_mode'),
  trackingCallback(experiment, result) {
    handleExperimentEvent({ experiment, result });
  }
});

async function fetchFeature() {
  try {
    const { features } = await getFeatures();
    growthbookInstance?.setFeatures(features);
    return features;
  } catch (error) {
    throw new Error('Error fetching GrowthBook features: ' + error);
  }
}

function setAttributes(params: growthBookParams) {
  growthbookInstance.setAttributes(params);
}

type GrowthbookContextProps = {
  children: React.ReactNode;
};

function GrowthbookContext({ children }: GrowthbookContextProps) {
  const pathname = usePathname();
  const params = useParams();

  useEffect(() => {
    async function initGrowthBook() {
      let sessionId = cookiesIdHandler().getSessionId();
      const eventId = params?.eventId || '0';
      if (!sessionId) sessionId = cookiesIdHandler().createSessionId();

      await fetchFeature();
      setAttributes({
        sessionId: sessionId as string,
        loggedIn: false,
        browser: navigator.userAgent,
        url: window.location.href,
        eventId: eventId as string
      });
    }

    initGrowthBook();
  }, [pathname]);

  return (
    <GrowthBookProvider growthbook={growthbookInstance}>
      {children}
    </GrowthBookProvider>
  );
}

export default GrowthbookContext;
